"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import React, { Suspense, useCallback, useMemo, useSyncExternalStore } from "react";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import BrainMobileTabs from "./mobile/BrainMobileTabs";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import BrainDesktopDrop from "./BrainDesktopDrop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { useWaveData } from "@/hooks/useWaveData";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import { useWave } from "@/hooks/useWave";
import { useWaveHasPolls } from "@/hooks/useWaveHasPolls";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import MemesQuickVoteDialog from "./left-sidebar/waves/memes-quick-vote/MemesQuickVoteDialog";
import { getActiveWaveIdFromUrl, getHomeRoute, getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { markDropCloseNavigation } from "@/helpers/drop-close-navigation.helpers";
import CreateWaveModal from "@/components/waves/create-wave/CreateWaveModal";
import CreateDirectMessageModal from "@/components/waves/create-dm/CreateDirectMessageModal";
import { useAuth } from "@/components/auth/Auth";
import { useMyStreamOptional } from "@/contexts/wave/MyStreamContext";
import { useClosingDropId } from "@/hooks/useClosingDropId";
import { useMemesQuickVoteDialogController } from "@/hooks/useMemesQuickVoteDialogController";
import BrainMobileViewContent from "./mobile/BrainMobileViewContent";
import { BrainView } from "./mobile/brainMobileViews";
import { useBrainMobileActiveView } from "./mobile/useBrainMobileActiveView";
import { DROP_DETAIL_STALE_TIME_MS, fetchDropByIdBatched, getDropQueryKey } from "@/services/api/drop-api";
interface Props {
  readonly children: ReactNode;
}
const BrainMobileContent: React.FC<Props> = t0 => {
  const $ = _c(103);
  const {
    children
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const {
    connectedProfile
  } = useAuth();
  const hasAuthenticatedProfile = Boolean(connectedProfile?.handle);
  const quickVote = useMemesQuickVoteDialogController();
  const hydrated = useSyncExternalStore(_temp2, _temp3, _temp4);
  const myStream = useMyStreamOptional();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("drop") ?? undefined;
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const dropId = t1;
  const {
    effectiveDropId,
    beginClosingDrop
  } = useClosingDropId(dropId);
  let t2;
  if ($[2] !== effectiveDropId) {
    t2 = getDropQueryKey(effectiveDropId);
    $[2] = effectiveDropId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== effectiveDropId) {
    t3 = () => {
      if (!effectiveDropId) {
        throw new Error("Cannot fetch drop without a drop id");
      }
      return fetchDropByIdBatched(effectiveDropId);
    };
    $[4] = effectiveDropId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = !!effectiveDropId;
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      placeholderData: keepPreviousData,
      enabled: t4,
      staleTime: DROP_DETAIL_STALE_TIME_MS
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    data: drop
  } = useQuery(t5);
  let t6;
  if ($[10] !== myStream?.activeWave.id || $[11] !== pathname || $[12] !== searchParams) {
    t6 = myStream?.activeWave.id ?? getActiveWaveIdFromUrl({
      pathname,
      searchParams
    }) ?? null;
    $[10] = myStream?.activeWave.id;
    $[11] = pathname;
    $[12] = searchParams;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const waveId = t6;
  let t7;
  if ($[14] !== isApp || $[15] !== pathname || $[16] !== router || $[17] !== searchParams) {
    t7 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      const basePath = getWaveHomeRoute({
        isDirectMessage: pathname.startsWith("/messages"),
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath || getHomeRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[14] = isApp;
    $[15] = pathname;
    $[16] = router;
    $[17] = searchParams;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t7 || $[20] !== waveId) {
    t8 = {
      waveId,
      onWaveNotFound: t7
    };
    $[19] = t7;
    $[20] = waveId;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const {
    data: wave
  } = useWaveData(t8);
  const {
    isMemesWave,
    isCurationWave,
    isRankWave,
    isApproveWave
  } = useWave(wave);
  const {
    voting: t9,
    decisions: t10
  } = useWaveTimers(wave);
  const {
    isCompleted
  } = t9;
  const {
    firstDecisionDone
  } = t10;
  const t11 = wave !== undefined;
  let t12;
  if ($[22] !== t11 || $[23] !== waveId) {
    t12 = {
      waveId,
      enabled: t11
    };
    $[22] = t11;
    $[23] = waveId;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const hasPolls = useWaveHasPolls(t12);
  let t13;
  if ($[25] !== firstDecisionDone || $[26] !== hasAuthenticatedProfile || $[27] !== hasPolls || $[28] !== isApp || $[29] !== isApproveWave || $[30] !== isCompleted || $[31] !== isCurationWave || $[32] !== isMemesWave || $[33] !== isRankWave || $[34] !== pathname || $[35] !== searchParams || $[36] !== wave || $[37] !== waveId) {
    t13 = {
      firstDecisionDone,
      isApp,
      isCompleted,
      hasAuthenticatedProfile,
      isCurationWave,
      isMemesWave,
      isRankWave,
      isApproveWave,
      hasPolls,
      pathname,
      searchParams,
      wave,
      waveId
    };
    $[25] = firstDecisionDone;
    $[26] = hasAuthenticatedProfile;
    $[27] = hasPolls;
    $[28] = isApp;
    $[29] = isApproveWave;
    $[30] = isCompleted;
    $[31] = isCurationWave;
    $[32] = isMemesWave;
    $[33] = isRankWave;
    $[34] = pathname;
    $[35] = searchParams;
    $[36] = wave;
    $[37] = waveId;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const {
    activeView,
    onViewChange
  } = useBrainMobileActiveView(t13);
  let t14;
  if ($[39] !== pathname || $[40] !== router || $[41] !== searchParams) {
    t14 = selectedDrop => {
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("drop", selectedDrop.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[39] = pathname;
    $[40] = router;
    $[41] = searchParams;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const onDropClick = t14;
  let t15;
  if ($[43] !== beginClosingDrop || $[44] !== dropId || $[45] !== pathname || $[46] !== router || $[47] !== searchParams) {
    t15 = () => {
      if (dropId) {
        beginClosingDrop(dropId);
      }
      markDropCloseNavigation();
      const params_1 = new URLSearchParams(searchParams.toString() || "");
      params_1.delete("drop");
      const newUrl_0 = params_1.toString() ? `${pathname}?${params_1.toString()}` : pathname || getHomeRoute();
      router.replace(newUrl_0, {
        scroll: false
      });
    };
    $[43] = beginClosingDrop;
    $[44] = dropId;
    $[45] = pathname;
    $[46] = router;
    $[47] = searchParams;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  const onDropClose = t15;
  let t16;
  if ($[49] !== drop || $[50] !== effectiveDropId) {
    t16 = !!effectiveDropId && !!drop && drop.id.toLowerCase() === effectiveDropId.toLowerCase();
    $[49] = drop;
    $[50] = effectiveDropId;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  const isDropOpen = t16;
  const hasWave = Boolean(waveId);
  let t17;
  if ($[52] !== pathname || $[53] !== searchParams) {
    t17 = () => {
      const params_2 = new URLSearchParams(searchParams.toString() || "");
      params_2.delete("create");
      const base = pathname || getHomeRoute();
      const next = params_2.toString() ? `${base}?${params_2.toString()}` : base;
      globalThis.window.history.replaceState(null, "", next);
    };
    $[52] = pathname;
    $[53] = searchParams;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  const closeCreateOverlay = t17;
  let t18;
  bb0: {
    if (!isApp) {
      t18 = null;
      break bb0;
    }
    const createParam = searchParams.get("create");
    if (!createParam) {
      t18 = null;
      break bb0;
    }
    if (!connectedProfile) {
      t18 = null;
      break bb0;
    }
    if (createParam === "dm") {
      let t19;
      if ($[55] !== closeCreateOverlay || $[56] !== connectedProfile) {
        t19 = <CreateDirectMessageModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[55] = closeCreateOverlay;
        $[56] = connectedProfile;
        $[57] = t19;
      } else {
        t19 = $[57];
      }
      t18 = t19;
      break bb0;
    }
    if (createParam === "wave") {
      let t19;
      if ($[58] !== closeCreateOverlay || $[59] !== connectedProfile) {
        t19 = <CreateWaveModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[58] = closeCreateOverlay;
        $[59] = connectedProfile;
        $[60] = t19;
      } else {
        t19 = $[60];
      }
      t18 = t19;
      break bb0;
    }
    t18 = null;
  }
  const createOverlay = t18;
  const shouldMountQuickVoteDialog = quickVote.isQuickVoteOpen || activeView === BrainView.WAVES;
  const dropOverlayClass = isApp ? "tw-fixed tw-inset-0 tw-z-[1010] tw-bg-black tailwind-scope" : "tw-absolute tw-inset-0 tw-z-[1010]";
  let t19;
  if ($[61] !== drop || $[62] !== dropOverlayClass || $[63] !== isDropOpen || $[64] !== onDropClose) {
    t19 = isDropOpen && <div className={dropOverlayClass}><BrainDesktopDrop drop={{
        type: DropSize.FULL,
        ...drop,
        stableKey: drop.id,
        stableHash: drop.id
      }} onClose={onDropClose} /></div>;
    $[61] = drop;
    $[62] = dropOverlayClass;
    $[63] = isDropOpen;
    $[64] = onDropClose;
    $[65] = t19;
  } else {
    t19 = $[65];
  }
  let t20;
  if ($[66] !== activeView || $[67] !== hasPolls || $[68] !== hasWave || $[69] !== hydrated || $[70] !== isApp || $[71] !== onViewChange || $[72] !== wave) {
    t20 = (hasWave || !isApp) && <BrainMobileTabs activeView={activeView} onViewChange={onViewChange} wave={wave} waveActive={hasWave} hasPolls={hasPolls} showWavesTab={hydrated} showStreamBack={hydrated} isApp={isApp} />;
    $[66] = activeView;
    $[67] = hasPolls;
    $[68] = hasWave;
    $[69] = hydrated;
    $[70] = isApp;
    $[71] = onViewChange;
    $[72] = wave;
    $[73] = t20;
  } else {
    t20 = $[73];
  }
  let t21;
  let t22;
  let t23;
  let t24;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      opacity: 0,
      x: 20
    };
    t22 = {
      opacity: 1,
      x: 0
    };
    t23 = {
      opacity: 0,
      x: -20
    };
    t24 = {
      duration: 0.2,
      ease: "easeInOut"
    };
    $[74] = t21;
    $[75] = t22;
    $[76] = t23;
    $[77] = t24;
  } else {
    t21 = $[74];
    t22 = $[75];
    t23 = $[76];
    t24 = $[77];
  }
  let t25;
  if ($[78] !== activeView || $[79] !== children || $[80] !== hasPolls || $[81] !== isApproveWave || $[82] !== isCurationWave || $[83] !== isMemesWave || $[84] !== isRankWave || $[85] !== onDropClick || $[86] !== quickVote.openQuickVote || $[87] !== quickVote.prefetchQuickVote || $[88] !== wave || $[89] !== waveId) {
    t25 = <BrainMobileViewContent activeView={activeView} activeWaveId={waveId} isCurationWave={isCurationWave} isMemesWave={isMemesWave} isRankWave={isRankWave} isApproveWave={isApproveWave} hasPolls={hasPolls} onDropClick={onDropClick} onOpenQuickVote={quickVote.openQuickVote} onPrefetchQuickVote={quickVote.prefetchQuickVote} wave={wave}>{children}</BrainMobileViewContent>;
    $[78] = activeView;
    $[79] = children;
    $[80] = hasPolls;
    $[81] = isApproveWave;
    $[82] = isCurationWave;
    $[83] = isMemesWave;
    $[84] = isRankWave;
    $[85] = onDropClick;
    $[86] = quickVote.openQuickVote;
    $[87] = quickVote.prefetchQuickVote;
    $[88] = wave;
    $[89] = waveId;
    $[90] = t25;
  } else {
    t25 = $[90];
  }
  let t26;
  if ($[91] !== activeView || $[92] !== t25) {
    t26 = <LazyMotion features={domAnimation}><AnimatePresence mode="wait"><m.div key={activeView} initial={t21} animate={t22} exit={t23} transition={t24} className="tw-relative tw-min-w-0 tw-flex-1">{t25}</m.div></AnimatePresence></LazyMotion>;
    $[91] = activeView;
    $[92] = t25;
    $[93] = t26;
  } else {
    t26 = $[93];
  }
  let t27;
  if ($[94] !== quickVote.dialogState || $[95] !== shouldMountQuickVoteDialog) {
    t27 = shouldMountQuickVoteDialog && <MemesQuickVoteDialog {...quickVote.dialogState} />;
    $[94] = quickVote.dialogState;
    $[95] = shouldMountQuickVoteDialog;
    $[96] = t27;
  } else {
    t27 = $[96];
  }
  let t28;
  if ($[97] !== createOverlay || $[98] !== t19 || $[99] !== t20 || $[100] !== t26 || $[101] !== t27) {
    t28 = <div className="tw-relative tw-flex tw-h-full tw-flex-col">{createOverlay}{t19}{t20}{t26}{t27}</div>;
    $[97] = createOverlay;
    $[98] = t19;
    $[99] = t20;
    $[100] = t26;
    $[101] = t27;
    $[102] = t28;
  } else {
    t28 = $[102];
  }
  return t28;
};
const BrainMobile: React.FC<Props> = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Suspense fallback={null}><BrainMobileContent {...props} /></Suspense>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export default BrainMobile;
function _temp() {}
function _temp2() {
  return _temp;
}
function _temp3() {
  return true;
}
function _temp4() {
  return false;
}